"use client";

import {
  useReducer,
  useEffect,
  useRef,
  useState,
  type ReactNode,
  useMemo,
} from "react";
import { useTranslation } from "react-i18next";
import {
  useDebouncedState,
  type FormFieldSchema,
  type SlidesRef,
} from "@huub-web/ui";
import { localStore, sessionStore } from "@huub-web/utils";

import type {
  MaximumMortgageWizardState,
  MortgageWizardAction,
  MaximumMortgageWizardFormValues,
  MaximumMortgageWizardSlide,
  MaximumMortgageFlowConfig,
} from "../../types/MaximumMortgageWizard";
import type { Bank } from "../../types/Bank";
import {
  MaximumMortgageWizardContext,
  type MaximumMortgageWizardContextType,
} from "../../contexts/MaximumMortgageWizardContext";
import { useBanks } from "../../hooks/useBanks";
import { IncomeSlide } from "../MaximumMortgageWizard/components/IncomeSlide";
import { PurposeSlide } from "../MaximumMortgageWizard/components/PurposeSlide";
import { CurrentHouseSlide } from "../MaximumMortgageWizard/components/CurrentHouseSlide";
import { FinancialObligationsSlide } from "../MaximumMortgageWizard/components/FinancialObligationsSlide";
import {
  NewHouseSlide,
  type NewHouseSlideProps,
} from "../MaximumMortgageWizard/components/NewHouseSlide";
import { filterMaximumMortgageWizardValues } from "../../helpers/filterMaximumMortgageWizardValues";

/**
 * Storage key for the in-progress wizard form values.
 *
 * These values include applicant financial PII (income, obligations). They are
 * persisted in `sessionStorage` (not `localStorage`) so they are scoped to the
 * tab/session, carry no long-lived at-rest footprint, and are dropped on tab
 * close; they are also explicitly cleared on submit (SMA-3172).
 */
const STORAGE_KEY = "maximumMortgageWizard";

/**
 * Reads the persisted wizard values, migrating any legacy `localStorage`
 * payload forward.
 *
 * Before SMA-3172 these values (income/obligation PII) were kept in
 * `localStorage`. Returning users still carry that long-lived copy, so we
 * prefer the `sessionStorage` value but fall back to the legacy `localStorage`
 * entry, copy it into `sessionStorage`, and delete the `localStorage` copy so
 * no financial PII lingers at rest.
 * @returns The raw persisted JSON string, or `null` when none exists
 */
const readAndMigrateWizardValues = (): string | null => {
  const sessionData = sessionStore.get(STORAGE_KEY);
  if (sessionData) {
    // A newer session payload already exists; drop any stale legacy copy.
    localStore.remove(STORAGE_KEY);
    return sessionData;
  }

  const legacyData = localStore.get(STORAGE_KEY);
  if (legacyData) {
    // Only drop the durable legacy copy once the session write has actually
    // succeeded — otherwise a failed sessionStorage write (quota / disabled)
    // would destroy the only durable wizard draft.
    if (sessionStore.set(STORAGE_KEY, legacyData)) {
      localStore.remove(STORAGE_KEY);
    }
    return legacyData;
  }

  return null;
};

/**
 * Removes the persisted wizard values from both session and legacy
 * `localStorage` so no financial PII lingers at rest after submit.
 */
const clearStoredWizardValues = (): void => {
  sessionStore.remove(STORAGE_KEY);
  localStore.remove(STORAGE_KEY);
};

const INITIAL_STATE: MaximumMortgageWizardState = {
  currentSlide: "income",
  formValues: {
    income: "",
    age: "18-34",
    hasPartner: false,
    hasLoans: false,
    hasStudentDebt: false,
    hasPrivateLease: false,
  },
  slideValidation: {
    income: false,
    purpose: false,
    currentHouse: false,
    financialObligations: false,
    newHouse: true,
  },
};

function mortgageWizardReducer(
  state: MaximumMortgageWizardState,
  action: MortgageWizardAction,
): MaximumMortgageWizardState {
  switch (action.type) {
    case "SET_SLIDE":
      return {
        ...state,
        currentSlide: action.payload,
      };

    case "UPDATE_FORM_VALUES":
      return {
        ...state,
        formValues: {
          ...state.formValues,
          ...action.payload,
        },
      };

    case "SET_SLIDE_VALIDATION":
      return {
        ...state,
        slideValidation: {
          ...state.slideValidation,
          [action.payload.slide]: action.payload.isValid,
        },
      };

    case "LOAD_FROM_STORAGE":
      return {
        ...state,
        formValues: {
          ...state.formValues,
          ...action.payload,
        },
      };

    default:
      return state;
  }
}

type MaximumMortgageWizardProviderProps = {
  children: ReactNode;
  loading?: boolean;
  /** Initial form values to populate the wizard (takes precedence over localStorage) */
  initialValues?: Partial<MaximumMortgageWizardFormValues>;
  onChange?: (formValues: Partial<MaximumMortgageWizardFormValues>) => void;
  onStateChange?: (state: MaximumMortgageWizardState) => void;
  onSubmit?: (
    formValues: Partial<MaximumMortgageWizardFormValues>,
  ) => void | Promise<void>;
  getSearchResults?: NewHouseSlideProps["getSearchResults"];
  getListing?: NewHouseSlideProps["getListing"];
  addressObjectID?: string;
  /**
   * Backend-driven flow configuration. When present, restricts
   * which mortgage types are available in the wizard.
   */
  flowConfig?: MaximumMortgageFlowConfig;
  /**
   * Async function that resolves to a list of banks/lenders.
   * When provided, the lender dropdown in PurposeSlide and CurrentHouseSlide
   * will be populated with the returned banks plus an "other" option.
   */
  getBanks?: () => Promise<Bank[]>;
  /**
   * Called when the enriched field schema changes — on field registration,
   * deregistration, and value changes. Provides the full schema with
   * fieldKey, label, type, step, and current value for AI agent awareness.
   */
  onFieldSchemaChange?: (
    slideKey: MaximumMortgageWizardSlide,
    schema: FormFieldSchema[],
  ) => void;
};

export const MaximumMortgageWizardProvider = ({
  loading = false,
  initialValues,
  onChange,
  onStateChange,
  onSubmit,
  onFieldSchemaChange,
  getSearchResults,
  getListing,
  addressObjectID,
  flowConfig,
  getBanks,
  children,
}: MaximumMortgageWizardProviderProps) => {
  const { t } = useTranslation();

  const { bankOptions, loading: banksLoading } = useBanks({
    getBanks,
    otherLabel: t("maxMortgageWizard.lenderOther"),
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Compute initial state with initialValues merged in (takes precedence over defaults)
  const computedInitialState = useMemo((): MaximumMortgageWizardState => {
    if (!initialValues) {
      return INITIAL_STATE;
    }

    return {
      ...INITIAL_STATE,
      formValues: {
        ...INITIAL_STATE.formValues,
        ...initialValues,
      },
    };
    // Only compute once on mount - initialValues should not change
  }, []);

  const [state, dispatch] = useReducer(
    mortgageWizardReducer,
    computedInitialState,
  );

  const isHydratedRef = useRef(false);

  // Once the wizard has been intentionally cleared on submit, further debounced
  // persistence must not resurrect the financial PII. A pending debounced write
  // (queued by a field edit just before submit) would otherwise rewrite the
  // storage after `clearStoredWizardValues`, undoing the clear.
  const isClearedRef = useRef(false);

  const slidesRef = useRef<SlidesRef>(null);
  const slideFormsRef = useRef<
    Record<
      MaximumMortgageWizardSlide,
      React.RefObject<HTMLFormElement | null> | null
    >
  >({
    income: null,
    purpose: null,
    currentHouse: null,
    financialObligations: null,
    newHouse: null,
  });

  // Debounced session-storage persistence
  const { setState: setDebouncedFormValues } = useDebouncedState(
    state.formValues,
    (formValues: Partial<MaximumMortgageWizardFormValues>) => {
      // Skip once cleared on submit so a stale pending write can't resurrect PII.
      if (isHydratedRef.current && !isClearedRef.current) {
        sessionStore.set(STORAGE_KEY, JSON.stringify(formValues));
      }
    },
    500,
  );

  // Mark as hydrated after first render
  useEffect(() => {
    isHydratedRef.current = true;
  }, []);

  // Load from localStorage only after hydration (skip if initialValues were provided)
  useEffect(() => {
    if (!isHydratedRef.current) return;

    // Skip localStorage loading if initialValues were provided
    // (they take precedence and are already in state)
    if (initialValues) return;

    // Prefer the session payload but migrate any legacy localStorage payload
    // forward (and remove it) so returning users leave no PII at rest.
    const savedData = readAndMigrateWizardValues();
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        dispatch({ type: "LOAD_FROM_STORAGE", payload: parsedData });
      } catch (error) {
        console.warn(
          "Failed to load mortgage wizard data from session storage:",
          error,
        );
      }
    }
  }, [isHydratedRef]);

  // Set addressObjectID when provided (overrides localStorage if present)
  useEffect(() => {
    if (
      addressObjectID &&
      addressObjectID !== state.formValues.walterAddressObjectID
    ) {
      // Only update and clear values if addressObjectID has changed
      dispatch({
        type: "UPDATE_FORM_VALUES",
        payload: {
          walterAddressObjectID: addressObjectID,
          houseInMind: "existing",
          // Clear old values so they can be refilled with new property data
          newHousePrice: "",
          newHouseEnergyLabel: undefined,
        },
      });
    }
  }, [addressObjectID, state.formValues.walterAddressObjectID]);

  // Update debounced state when form values change
  useEffect(() => {
    setDebouncedFormValues(state.formValues);
  }, [state.formValues, setDebouncedFormValues]);

  // Call onChange callback when form values change
  useEffect(() => {
    if (onChange) {
      onChange(state.formValues);
    }
  }, [state.formValues, onChange]);

  // Call onStateChange callback when state changes
  useEffect(() => {
    if (onStateChange) {
      onStateChange(state);
    }
  }, [state, onStateChange]);

  const updateFormValues = (
    values: Partial<MaximumMortgageWizardFormValues>,
  ) => {
    dispatch({ type: "UPDATE_FORM_VALUES", payload: values });
  };

  const setSlideValidation = (
    slide: MaximumMortgageWizardSlide,
    isValid: boolean,
  ) => {
    dispatch({ type: "SET_SLIDE_VALIDATION", payload: { slide, isValid } });
  };

  /**
   * ⚠️ IMPORTANT: When modifying this slideOrder logic, you MUST update
   * filterMaximumMortgageWizardValues.ts to ensure form data is filtered correctly.
   */
  const slideOrder = useMemo((): MaximumMortgageWizardSlide[] => {
    const slides: MaximumMortgageWizardSlide[] = ["income", "purpose"];

    // Add newHouse slide only for finance purpose and when user has house in mind
    if (
      state.formValues.purpose === "finance" &&
      state.formValues.houseInMind !== "no"
    ) {
      slides.push("newHouse");
    }

    // Add currentHouse slide only for finance purpose
    if (state.formValues.purpose === "finance") {
      slides.push("currentHouse");
    }

    slides.push("financialObligations");

    return slides;
  }, [state.formValues.purpose, state.formValues.houseInMind]);

  const currentSlideIndex = useMemo(
    () => slideOrder.indexOf(state.currentSlide),
    [slideOrder, state.currentSlide],
  );

  const canNavigateNext = (() => {
    // Can't navigate past last slide
    if (currentSlideIndex >= slideOrder.length - 1) return false;

    // Current slide must be valid
    return state.slideValidation[state.currentSlide];
  })();

  const canNavigatePrevious = currentSlideIndex > 0;

  const slides: Partial<
    Record<
      MaximumMortgageWizardSlide,
      { slug?: string; component: ReactNode } | ReactNode | null
    >
  > = (() => {
    const allSlides: Partial<
      Record<
        MaximumMortgageWizardSlide,
        { slug?: string; component: ReactNode } | ReactNode | null
      >
    > = {
      income: {
        slug: "inkomen",
        component: <IncomeSlide />,
      },
      purpose: {
        slug: "doel",
        component: <PurposeSlide />,
      },
      newHouse: {
        slug: "nieuwe-woning",
        component: (
          <NewHouseSlide
            getSearchResults={getSearchResults}
            getListing={getListing}
          />
        ),
      },
      currentHouse: {
        slug: "huidige-woning",
        component: <CurrentHouseSlide />,
      },
      financialObligations: {
        slug: "financiele-verplichtingen",
        component: <FinancialObligationsSlide />,
      },
    };

    // Remove currentHouse slide unless purpose is finance
    if (state.formValues.purpose !== "finance") {
      delete allSlides.currentHouse;
    }

    // Remove newHouse slide unless purpose is finance and user has house in mind
    if (
      state.formValues.purpose !== "finance" ||
      state.formValues.houseInMind === "no"
    ) {
      delete allSlides.newHouse;
    }

    return allSlides;
  })();

  const registerForm = (
    slide: MaximumMortgageWizardSlide,
    formRef: React.RefObject<HTMLFormElement | null>,
  ) => {
    slideFormsRef.current[slide] = formRef;
  };

  const submitCurrentForm = () => {
    const currentFormRef = slideFormsRef.current[state.currentSlide];
    if (currentFormRef?.current) {
      currentFormRef.current.requestSubmit();
    }
  };

  const navigateToNext = () => {
    const nextIndex = currentSlideIndex + 1;

    if (nextIndex < slideOrder.length) {
      const nextSlide = slideOrder[nextIndex];
      dispatch({ type: "SET_SLIDE", payload: nextSlide });
      slidesRef.current?.next();
    } else if (onSubmit) {
      // Last slide - submit the wizard with filtered values
      const filteredValues = filterMaximumMortgageWizardValues(
        state.formValues,
      );

      // Drop the persisted financial PII, but only once the submit has actually
      // succeeded (SMA-3172). Clearing before success would lose the draft if an
      // async calculate fails while the user stays on the wizard. The guard is
      // set atomically with the clear so any pending debounced write is
      // neutralised and cannot rewrite the storage afterwards.
      const clearPersistedValues = () => {
        isClearedRef.current = true;
        clearStoredWizardValues();
      };

      try {
        const result = onSubmit(filteredValues);
        if (result instanceof Promise) {
          setIsSubmitting(true);
          result
            .then(() => clearPersistedValues())
            .catch(() => {})
            .finally(() => setIsSubmitting(false));
        } else {
          // Synchronous handler returned without throwing — treat as success
          // and drop the draft.
          clearPersistedValues();
        }
      } catch {
        // A synchronous handler that threw did not submit successfully — keep
        // the persisted draft so the user does not lose their input, matching
        // the async rejection path above (SMA-3172).
      }
    }
  };

  const handleNext = () => {
    submitCurrentForm();
  };

  const handlePrevious = () => {
    const prevIndex = currentSlideIndex - 1;

    if (prevIndex >= 0) {
      const prevSlide = slideOrder[prevIndex];
      dispatch({ type: "SET_SLIDE", payload: prevSlide });
      slidesRef.current?.previous();
    }
  };

  const handleSubmit = () => {
    submitCurrentForm();
  };

  // Handle case where current slide is not in the slide order
  useEffect(() => {
    if (currentSlideIndex === -1 && slideOrder.length > 0) {
      // Current slide is not available, navigate to the first available slide
      const firstAvailableSlide = slideOrder[0];
      dispatch({ type: "SET_SLIDE", payload: firstAvailableSlide });
    }
  }, [currentSlideIndex, slideOrder, dispatch]);

  const contextValue: MaximumMortgageWizardContextType = {
    state,
    dispatch,
    updateFormValues,
    setSlideValidation,
    slideOrder,
    currentSlideIndex,
    canNavigateNext,
    canNavigatePrevious,
    slides,
    slidesRef,
    loading: loading || isSubmitting,
    handleNext,
    handlePrevious,
    handleSubmit,
    navigateToNext,
    registerForm,
    submitCurrentForm,
    flowConfig,
    bankOptions,
    banksLoading,
    onFieldSchemaChange,
  };

  return (
    <MaximumMortgageWizardContext.Provider value={contextValue}>
      {children}
    </MaximumMortgageWizardContext.Provider>
  );
};
